import getQueryStringParameter from '../utilities/getQueryStringParameter';
import { post } from '../utilities/fetch';
import initiateTaxiAndLimoCurator, { IVehicle } from './taxi-and-limo-curator';
import { toggleError } from '../forms/book-taxi-limo';
import setQueryStringParameter from '../utilities/setQueryStringParameter';
import { createMap, getDirectionsFromAirport } from './taxi-and-limo-map';
import dataLayerPush from '../utilities/dataLayerPush';
import { getLanguage } from '../utilities/language';
import { getPlaceFromAddress } from '../utilities/getPlaceFromAddress';
import formIntent from '../utilities/formIntent';

interface IApiResult {
	Address: string;
	ErrorCode: string | null;
	VehicleTypes: IVehicle[];
}

const SEARCH_BOX_CONTAINER_SELECTOR = '[data-taxi-and-limo-search]';
const SEARCH_BOX_SELECTOR = '[data-taxi-and-limo-search-input]';

const API_ATTRIBUTE = 'data-api';
const LOGGING_API_ATTRIBUTE = 'data-log-usage-api';
const DATA_SOURCE_ID_ATTRIBUTE = 'data-source-id';

const DEFAULT_OPTIONS = {
	componentRestrictions: { country: ['ca', 'us'] }
};

let map: google.maps.Map;
let place: google.maps.places.PlaceResult | null = null;

export function main() {
	const form = document.querySelector<HTMLFormElement>(SEARCH_BOX_CONTAINER_SELECTOR);

	if (!form) {
		return;
	}

	map = createMap();

	initiateSearch(form);

	form.addEventListener('submit', (e: Event) => {
		e.preventDefault();

		onFormSubmit(form, place);
	});

	formIntent(form, () => {
		dataLayerPush({
			event: 'Taxi and limo search intent'
		});
	});
}

async function initiateSearch(form: HTMLFormElement) {
	const search = form.querySelector<HTMLInputElement>(SEARCH_BOX_SELECTOR);

	if (!search) {
		return;
	}

	setupAutocomplete(form);

	const address = getQueryStringParameter('address', window.location.href);

	if (address) {
		// We can assume the address is valid because we were directed from the taxi-limo widget
		place = await getPlaceFromAddress(address, map);

		setInputValue(form, search, address);
		onFormSubmit(form, place);
	}
}

function setupAutocomplete(form: HTMLFormElement) {
	const search = form.querySelector<HTMLInputElement>(SEARCH_BOX_SELECTOR)!;

	if (!search) {
		return;
	}

	const autocomplete = new google.maps.places.Autocomplete(search, DEFAULT_OPTIONS);

	// When a user clicks an address suggestion
	autocomplete.addListener('place_changed', () => {
		search.dispatchEvent(new Event('change'));

		const invalidInputErrorMessage = form.querySelector<HTMLElement>('[data-search-address-invalid]');

		place = autocomplete.getPlace();

		// If place does not have a valid place_id, show invalidInputErrorMessage
		if (place.place_id) {
			showQuote(form, place);

			toggleError(false, invalidInputErrorMessage!);
		} else {
			onFormSubmit(form, place);
		}
	});
}

async function onFormSubmit(form: HTMLFormElement, place) {
	const search = form.querySelector<HTMLInputElement>('[data-taxi-and-limo-search-input]');
	const invalidInputErrorMessage = form.querySelector<HTMLElement>('[data-search-address-invalid]');

	if (!search || !search.value) {
		return;
	}

	place = await getPlaceFromAddress(search.value, map);

	if (place) {
		toggleError(false, invalidInputErrorMessage!);

		showQuote(form, place);
	} else {
		displayPanel(false);
		hideAllErrors(form);
		toggleError(true, invalidInputErrorMessage!);

		dataLayerPush({
			event: 'Taxi and limo invalid address'
		});
	}
}

/**
 * If there's an address parameter in the URL,
 * set the value of the search box as the address and submit form
 * @param form HTMLFormElement
 * @param search HTMLInputElement
 */
async function setInputValue(form: HTMLFormElement, search: HTMLInputElement, address: string) {
	const dataSourceId = form.getAttribute(DATA_SOURCE_ID_ATTRIBUTE)!;
	const api = form.getAttribute(API_ATTRIBUTE)!;

	search.value = address;

	const data = await getData(api, address, dataSourceId);

	displayErrorMessages(form, data.ErrorCode);
}

async function showQuote(form: HTMLFormElement, place: google.maps.GeocoderResult | google.maps.places.PlaceResult) {
	const api = form.getAttribute(API_ATTRIBUTE);

	if (!api) {
		return;
	}

	const logApi = form.getAttribute(LOGGING_API_ATTRIBUTE);

	if (!logApi) {
		return;
	}

	const dataSourceId = form.getAttribute(DATA_SOURCE_ID_ATTRIBUTE)!;
	const invalidInputErrorMessage = form.querySelector<HTMLElement>('[data-search-address-invalid]');
	const searchButton = form.querySelector<HTMLButtonElement>('.search-box__button');

	// If formatted_address exists
	if (place.formatted_address) {
		toggleError(false, invalidInputErrorMessage!);

		try {
			if (searchButton) {
				showLoading(searchButton);
			}

			const data = await getData(api, place.formatted_address, dataSourceId);
			const errorExists = displayErrorMessages(form, data.ErrorCode);

			// Update url with new address
			const newUrl = setQueryStringParameter('address', data.Address, window.location.href);

			displayPanel(!errorExists);

			if (!errorExists) {
				history.pushState(null, '', newUrl);

				if (map && place) {
					getDirectionsFromAirport(map, place);
				}

				// Generate carousel cards
				initiateTaxiAndLimoCurator(data.VehicleTypes);
			}

			logSearch(logApi, place);
		} catch (c) {
			console.error(c);
		} finally {
			if (searchButton) {
				hideLoading(searchButton);
			}
		}
	} else {
		// Else show invalidInputErrorMessage
		toggleError(true, invalidInputErrorMessage!);
	}
}

async function getData(api: string, address: string, dataSourceId: string) {
	const key = `Taxi and Limo ${address} ${getLanguage()}`;

	try {
		if (sessionStorage.getItem(key)) {
			return JSON.parse(sessionStorage.getItem(key)!) as IApiResult;
		}
	} catch (e) {
		console.log('Error occured trying to get the session storage after getting quote data.');
	}

	dataLayerPush({
		event: 'Taxi and limo form submit'
	});

	const response = await post(api, { address, dataSourceId });
	const data = (await response.json()) as IApiResult;

	try {
		sessionStorage.setItem(key, JSON.stringify(data));
	} catch (e) {
		console.log('Error occured trying to set the session storage after getting quote data.');
	}

	return data;
}

async function logSearch(api: string, place: google.maps.GeocoderResult | google.maps.places.PlaceResult) {
	if (!place.place_id) {
		return;
	}

	const placeDetails = await getPlaceDetails(place.place_id);

	const body = {
		postalCode: placeDetails
			.address_components!.filter((address) => address.types.find((address) => address === 'postal_code'))
			.map((address) => address.long_name)
			.toString(),
		latitude: place.geometry!.location!.lat(),
		longitude: place.geometry!.location!.lng(),
		placeId: place.place_id
	};

	post(api, body);
}

export function getPlaceDetails(placeId: string): Promise<google.maps.places.PlaceResult> {
	const key = `Taxi and Limo ${placeId} ${getLanguage()}`;

	return new Promise((resolve, reject) => {
		try {
			const result = sessionStorage.getItem(key);

			if (result) {
				return resolve(JSON.parse(result) as google.maps.places.PlaceResult);
			}
		} catch (e) {
			console.log('Failed to retrieve place ID adresss component from session storage.');
		}

		const service = new google.maps.places.PlacesService(map);

		service.getDetails({ placeId, fields: ['address_components'] }, (result, status) => {
			if (status === google.maps.places.PlacesServiceStatus.OK) {
				try {
					sessionStorage.setItem(key, JSON.stringify(result));
				} catch (e) {
					console.log('Failed to set place ID address component in session storage.');
				}

				resolve(result);
			} else {
				reject();
			}
		});
	});
}

function displayErrorMessages(form: HTMLFormElement, errorMessage: string | null) {
	const serviceNotAvailable = form.querySelector<HTMLElement>('[data-service-unavailable]')!;
	const noDataForAddress = form.querySelector<HTMLElement>('[data-no-data-for-address]')!;

	if (errorMessage === 'no.data.for.address') {
		hideAllErrors(form);
		toggleError(true, noDataForAddress);

		dataLayerPush({
			event: 'Taxi and limo out of range'
		});

		return true;
	} else if (errorMessage === 'service.unavailable') {
		hideAllErrors(form);
		toggleError(true, serviceNotAvailable);

		dataLayerPush({
			event: 'Taxi and limo service unavailable'
		});

		return true;
	} else {
		hideAllErrors(form);
	}

	return false;
}

function displayPanel(display: boolean, TAXI_AND_LIMO_PANEL_SELECTOR = '.taxi-and-limo-panel') {
	const panel = document.querySelector(TAXI_AND_LIMO_PANEL_SELECTOR);

	if (!panel) {
		return;
	}

	if (display) {
		panel.classList.add('taxi-and-limo-panel--loaded');
		panel.setAttribute('aria-hidden', 'false');
	} else {
		panel.classList.remove('taxi-and-limo-panel--loaded');
		panel.setAttribute('aria-hidden', 'true');
	}
}

function showLoading(button: HTMLButtonElement) {
	button.classList.add('search-box__button--loading');
	button.setAttribute('disabled', 'true');
}

function hideLoading(button: HTMLButtonElement) {
	button.classList.remove('search-box__button--loading');
	button.removeAttribute('disabled');
}

export function hideAllErrors(form: HTMLFormElement) {
	const serviceNotAvailable = form.querySelector<HTMLElement>('[data-service-unavailable]')!;
	const noDataForAddress = form.querySelector<HTMLElement>('[data-no-data-for-address]')!;
	const invalidInputErrorMessage = form.querySelector<HTMLElement>('[data-search-address-invalid]')!;

	toggleError(false, serviceNotAvailable);
	toggleError(false, noDataForAddress);
	toggleError(false, invalidInputErrorMessage);
}

main();
